import { queryOptions } from '@tanstack/react-query';

import { ONE_MINUTE } from '@/constants/time';
import { paths } from '@/types/api';
import { fetchClient } from '@/util/fetchClient';

/**
 * Type representing a single featured expert item from the API.
 * Contains the expert's public summary, associated cancer type, and optional description.
 */
export type ActiveFeaturedExpertItem = NonNullable<
  paths['/v2/featured-experts']['get']['responses'][200]['content']['application/json']['data']
>[number];

/**
 * Fetches all currently active featured experts from the API.
 * Returns experts whose featured period includes the current date.
 *
 * @returns Array of featured expert items, or empty array if none exist
 */
export async function getFeaturedExpertsV2(): Promise<ActiveFeaturedExpertItem[]> {
  const { data } = await fetchClient.GET('/v2/featured-experts');

  return data?.data ?? [];
}

/**
 * React Query options factory for fetching featured experts.
 *
 * Cache strategy rationale:
 * - staleTime: 5 minutes - Featured experts are manually curated by admins and rarely change
 *   during a typical user session. Background updates check for changes without blocking UI.
 * - gcTime: 10 minutes - Retains cached data for navigation back to directory without refetch.
 *   Featured expert list is small (~5-10 items) so memory impact is negligible.
 *
 * Business context: Featured experts highlight specific specialists for promotional campaigns
 * or emerging treatment areas. Changes typically occur weekly during content planning cycles.
 *
 * @returns Query options for use with useQuery or prefetchQuery
 */
export function getFeaturedExpertsV2QueryOptions() {
  return queryOptions({
    queryKey: ['featuredExpertsV2'],
    queryFn: getFeaturedExpertsV2,
    staleTime: 5 * ONE_MINUTE,
    gcTime: 10 * ONE_MINUTE,
  });
}
